import styles from '../marketplace.module.css'

import {useRoutePayload} from '@github-ui/react-core/use-route-payload'

import {Heading, PageLayout, Text, UnderlineNav} from '@primer/react'
import MarketplaceHeader from '../components/MarketplaceHeader'
import MarketplaceNavigation from '../components/MarketplaceNavigation'
import MarketplaceItem from '../components/MarketplaceItem'
import type {IndexPayload} from '../types'
import {useMemo, useState} from 'react'
import {useSearchParams} from '@github-ui/use-navigate'
import SearchResults from '../components/SearchResults'

export function Index() {
  const payload = useRoutePayload<IndexPayload>()
  const [searchParams] = useSearchParams()
  const [currentList, setCurrentList] = useState<'recommended' | 'recentlyAdded'>('recommended')

  const searchActive = useMemo(() => {
    if (searchParams.size === 0) {
      return false
    }

    return (
      searchParams.get('query') ||
      searchParams.get('category') ||
      searchParams.get('type') ||
      searchParams.get('copilot_app')
    )
  }, [searchParams])

  return (
    <>
      <MarketplaceHeader />
      <PageLayout>
        <PageLayout.Pane position="start">
          <MarketplaceNavigation categories={payload.categories} />
        </PageLayout.Pane>
        <PageLayout.Content as="div">
          {searchActive ? (
            <SearchResults searchResults={payload.searchResults} categories={payload.categories} />
          ) : (
            <>
              <Heading as="h2" className="f2">
                Discover apps with Copilot extensions
              </Heading>
              <Text className="fgColor-muted">Your favorite tools now work with GitHub Copilot</Text>
              <div className={`mt-4 ${styles['marketplace-featured-grid']}`}>
                {payload.featured.map(listing => (
                  <MarketplaceItem key={`${listing.type}-${listing.id}`} listing={listing} isFeatured={true} />
                ))}
              </div>
              <UnderlineNav
                aria-label="View recommended or recent marketplace listings"
                sx={{mt: 4, pl: 0, '& button': {padding: '0.375rem 0.5rem !important'}}}
              >
                <UnderlineNav.Item
                  as="button"
                  aria-current={currentList === 'recommended' ? 'page' : undefined}
                  onSelect={event => {
                    event?.preventDefault()
                    setCurrentList('recommended')
                  }}
                >
                  Recommended
                </UnderlineNav.Item>
                <UnderlineNav.Item
                  as="button"
                  aria-current={currentList === 'recentlyAdded' ? 'page' : undefined}
                  onSelect={event => {
                    event?.preventDefault()
                    setCurrentList('recentlyAdded')
                  }}
                >
                  Recently added
                </UnderlineNav.Item>
              </UnderlineNav>
              {currentList === 'recommended' && (
                <div className={`mt-4 ${styles['marketplace-list-grid']}`}>
                  {payload.recommended.map(listing => (
                    <MarketplaceItem key={`${listing.type}-${listing.id}`} listing={listing} isFeatured={false} />
                  ))}
                </div>
              )}
              {currentList === 'recentlyAdded' && (
                <div className={`mt-4 ${styles['marketplace-list-grid']}`}>
                  {payload.recentlyAdded.map(listing => (
                    <MarketplaceItem key={`${listing.type}-${listing.id}`} listing={listing} isFeatured={false} />
                  ))}
                </div>
              )}
            </>
          )}
        </PageLayout.Content>
      </PageLayout>
    </>
  )
}

try{ Index.displayName ||= 'Index' } catch {}